import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import CoinBadge from '../../components/CoinBadge/CoinBadge';
import CoinModal from '../../components/CoinModal/CoinModal';
import AvatarModal from '../../components/AvatarModal/AvatarModal';
import CTAButton from '../../components/CTAButton/CTAButton';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import ShareButtons from '../../components/ShareButtons/ShareButtons';
import StickyMobileCTA from '../../components/StickyMobileCTA/StickyMobileCTA';
import { SocialService } from '../../services/socialService';
import PublicListsSection from '../../components/MyLists/PublicListsSection';
import TopMoviesProfileSection from '../../components/TopMovies/TopMoviesProfileSection';
import RecentlyRatedSection from '../../components/RatedMovies/RecentlyRatedSection';
import RankingsProfileSection from '../../components/Rankings/RankingsProfileSection';
import useProfileRankings from '../../components/Rankings/useProfileRankings';
import SafetyService from '../../services/safetyService';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../../components/AuthModal/AuthModal';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import styles from './Profile.module.css';
import { FoundingCollectorIcon, FoundingCollectorLabel } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';

function Profile() {
    const { username: rawUsername } = useParams();
    // Remove @ symbol if present
    const username = rawUsername?.startsWith('@')
        ? rawUsername.slice(1)
        : rawUsername;
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [profileUser, setProfileUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [following, setFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('coins'); // 'coins' or 'watchlist'
    const [coinFilter, setCoinFilter] = useState('personal'); // 'personal' or 'legendary'
    const [watchlistFilter, setWatchlistFilter] = useState('all'); // 'all', 'movie', 'tv'
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [isCoinModalOpen, setIsCoinModalOpen] = useState(false);
    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

    const isOwnProfile = user?.username === username;
    // Public rankings only; waits for the profile so a private one never asks.
    const profileRankings = useProfileRankings(username, { enabled: Boolean(profileUser) && !profileUser.isPrivate });

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    useEffect(() => {
        const loadProfile = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await SocialService.getPublicProfile(username);
                console.log('Profile response:', response);
                setProfileUser(response);
                setFollowing(response.isFollowing || false);
            } catch (err) {
                console.error('Failed to load profile:', err);
                setError(err.message || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            loadProfile();
        }
    }, [username]);

    const handleBlock = async () => {
        if (!window.confirm(`Block @${profileUser.username}? They won't be able to see your profile or interact with you.`)) return;
        try {
            await SafetyService.blockUser(profileUser.id);
            window.location.href = '/';
        } catch (err) {
            alert('Could not block user. Please try again.');
        }
    };

    const doFollow = async () => {
        if (!profileUser?.id) return;

        setFollowLoading(true);
        try {
            if (following) {
                await SocialService.unfollow(profileUser.id);
                setFollowing(false);
            } else {
                await SocialService.follow(profileUser.id);
                setFollowing(true);
            }
        } catch (err) {
            console.error('Failed to update follow status:', err);
        } finally {
            setFollowLoading(false);
        }
    };

    const handleFollow = gateAction(doFollow, AUTH_MESSAGES.FOLLOW, {
        type: 'FOLLOW_USER',
        returnUrl: window.location.pathname + window.location.search + window.location.hash,
        userId: profileUser?.id,
    });

    const handleCoinClick = (coin) => {
        setSelectedCoin(coin);
        setIsCoinModalOpen(true);
    };

    const handleCloseCoinModal = () => {
        setIsCoinModalOpen(false);
        setSelectedCoin(null);
    };

    const handleAvatarClick = () => {
        if (profileUser?.avatar_url) {
            setIsAvatarModalOpen(true);
        }
    };

    if (loading) return <ProfileSkeleton />;
    if (error || !profileUser) {
        return (
            <div className={styles.errorState}>
                <span style={{ fontSize: '2.5rem' }}>👤</span>
                <p>User not found.</p>
                <Link to="/" className={styles.errorLink}>← Back to Home</Link>
            </div>
        );
    }

    const profileUrl = `${window.location.origin}/u/${profileUser.username}`;
    const shareTitle = `Check out @${profileUser.username}'s verified movie & TV profile on Aluvo — ${profileUser.coinCount ?? 0} verified coins earned!`;

    const coins = profileUser.coins || [];
    const publicLists = profileUser.publicLists || [];
    const watchlistItems = profileUser.watchlist?.items || [];
    const watchlistVisible = profileUser.watchlist?.items !== null;

    // Separate personal and legendary coins. Group by sourceType (matches MyProfile):
    // collection coins carry the collection's reward tier as rarity, which can be 'legendary'.
    const personalCoins = coins.filter(coin => coin.sourceType === 'media' && coin.rarity !== 'legendary');
    const legendaryCoins = coins.filter(coin =>
        (coin.sourceType === 'media' && coin.rarity === 'legendary') ||
        coin.sourceType === 'series_mastery'
    );
    const collectionCoins = coins.filter(coin => coin.sourceType === 'collection');

    // Filter watchlist based on selected filter
    const filteredWatchlist = watchlistItems.filter(item => {
        if (watchlistFilter === 'all') return true;
        return item.media?.kind === watchlistFilter;
    });

    return (
        <main className={styles.page}>
            <StickyMobileCTA href="#download" />

            <div className="container">
                {/* ── Profile header ── */}
                <div className={styles.profileHeader}>
                    <div className={styles.avatarWrap}>
                        {profileUser.avatar_url ? (
                            <img
                                src={profileUser.avatar_url}
                                alt={profileUser.display_name}
                                className={styles.avatar}
                                onClick={handleAvatarClick}
                                style={{ cursor: 'pointer' }}
                            />
                        ) : (
                            <div className={`${styles.avatar} ${styles.avatarFallback}`}>
                                {(profileUser.display_name || profileUser.username).charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div className={styles.profileInfo}>
                        <h1 className={styles.displayName}>
                            {profileUser.display_name || profileUser.username}
                            <FoundingCollectorIcon isFoundingCollector={profileUser.isFoundingCollector} size="md" />
                        </h1>
                        <p className={styles.usernameTag}>@{profileUser.username}</p>
                        <FoundingCollectorLabel isFoundingCollector={profileUser.isFoundingCollector} />
                        {profileUser.bio && <p className={styles.bio}>{profileUser.bio}</p>}
                        {/* Stats row */}
                        <div className={styles.statsRow}>
                            <div className={styles.statBox}>
                                <span className={styles.statIcon}>🪙</span>
                                <span className={styles.statValue}>{profileUser.coinCount ?? 0}</span>
                                <span className={styles.statLabel}>Coins</span>
                            </div>
                            <div className={styles.statDivider} />
                            <Link to={`/user/${profileUser.username}/followers`} className={styles.statBox}>
                                <span className={styles.statIcon}>👥</span>
                                <span className={styles.statValue}>{profileUser.stats?.followersCount ?? 0}</span>
                                <span className={styles.statLabel}>Followers</span>
                            </Link>
                            <div className={styles.statDivider} />
                            <Link to={`/user/${profileUser.username}/following`} className={styles.statBox}>
                                <span className={styles.statIcon}>➡️</span>
                                <span className={styles.statValue}>{profileUser.stats?.followingCount ?? 0}</span>
                                <span className={styles.statLabel}>Following</span>
                            </Link>
                        </div>

                        <div className={styles.shareRow}>
                            <ShareButtons url={profileUrl} title={shareTitle} label="Share public trophy case:" />
                            <Link to={`/u/${profileUser.username}`} className={styles.publicTrophyLink}>
                                View share page →
                            </Link>
                        </div>
                    </div>
                    <div className={styles.headerCta}>
                        {isOwnProfile ? (
                            <Link to="/me" className={styles.editProfileBtn}>Edit Profile</Link>
                        ) : (
                            <div className={styles.ctaGroup}>
                                <button
                                    className={`${styles.followBtn} ${following ? styles.followingBtn : ''}`}
                                    onClick={handleFollow}
                                    disabled={followLoading}
                                >
                                    {followLoading
                                        ? <span className={styles.followSpinner} />
                                        : following ? 'Following' : 'Follow'
                                    }
                                </button>
                                {user && (
                                    <div className={styles.safetyActions}>
                                        <button className={styles.safetyBtn} onClick={() => setIsReportModalOpen(true)}>
                                            Report
                                        </button>
                                        <button className={`${styles.safetyBtn} ${styles.safetyBtnDanger}`} onClick={handleBlock}>
                                            Block
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Tabs ── */}
                {/* Only a PUBLIC Top Movies list is ever sent by the API. */}
                <TopMoviesProfileSection topMovies={profileUser.topMovies} className={styles.section} />
                {/* Public rankings only, and only when the profile is viewable; none → hidden. */}
                <RankingsProfileSection
                    items={profileRankings.items}
                    hasMore={profileRankings.hasMore}
                    seeAllTo={`/user/${encodeURIComponent(profileUser.username)}/rankings`}
                    sourceScreen="profile"
                    loggedIn={!!user}
                    className={styles.section}
                />
                {/* recentRatings is only sent when the profile is viewable; empty → hidden for visitors. */}
                <RecentlyRatedSection
                    items={profileUser.isPrivate ? null : (profileUser.recentRatings ?? null)}
                    isOwner={isOwnProfile}
                    seeAllTo={`/user/${encodeURIComponent(profileUser.username)}/ratings`}
                    sourceScreen="profile"
                    loggedIn={!!user}
                    className={styles.section}
                />

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'coins' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('coins')}
                    >
                        🪙 Coins ({coins.length})
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'watchlist' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('watchlist')}
                    >
                        📺 Watchlist {watchlistVisible ? `(${watchlistItems.length})` : '🔒'}
                    </button>
                    {publicLists.length > 0 && (
                        <button
                            className={`${styles.tab} ${activeTab === 'lists' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('lists')}
                        >
                            📝 Lists ({publicLists.length})
                        </button>
                    )}
                </div>

                {/* ── Public Lists Tab (only PUBLIC My Lists are ever sent by the API) ── */}
                {activeTab === 'lists' && (
                    <PublicListsSection lists={publicLists} username={profileUser.username} heading="Public lists" className={styles.section} />
                )}

                {/* ── Coins Tab ── */}
                {activeTab === 'coins' && (
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <h2 className={styles.sectionTitle}>🪙 Coin Collection</h2>
                            <span className={styles.sectionCount}>{coins.length} earned</span>
                        </div>

                        {/* Coin Type Pills */}
                        <div className={styles.filterPills}>
                            <button
                                className={`${styles.filterPill} ${coinFilter === 'personal' ? styles.filterPillActive : ''}`}
                                onClick={() => setCoinFilter('personal')}
                            >
                                🪙 Personal Coins ({personalCoins.length})
                            </button>
                            <button
                                className={`${styles.filterPill} ${coinFilter === 'legendary' ? styles.filterPillActive : ''}`}
                                onClick={() => setCoinFilter('legendary')}
                            >
                                💎 Legendary Coins ({legendaryCoins.length})
                            </button>
                            <button
                                className={`${styles.filterPill} ${coinFilter === 'collection' ? styles.filterPillActive : ''}`}
                                onClick={() => setCoinFilter('collection')}
                            >
                                🎴 Collection Coins ({collectionCoins.length})
                            </button>
                        </div>

                        {/* Personal Coins View */}
                        {coinFilter === 'personal' && (
                            <>
                                {personalCoins.length > 0 && (
                                    <p className={styles.coinExplainer}>
                                        Each coin was earned by passing a challenge after watching — not bought, not given.
                                    </p>
                                )}
                                {personalCoins.length === 0 ? (
                                    <EmptyCoins message="No personal coins yet — take your first challenge to start earning coins." />
                                ) : (
                                    <div className={styles.coinGrid}>
                                        {personalCoins.map((coin) => (
                                            <CoinBadge
                                                key={coin.id}
                                                badge={coin}
                                                size="md"
                                                showLabel={true}
                                                onClick={handleCoinClick}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Legendary Coins View */}
                        {coinFilter === 'legendary' && (
                            <>
                                {legendaryCoins.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--color-text-muted)' }}>
                                        <span style={{ fontSize: '2rem' }}>💎</span>
                                        <p style={{ fontSize: '0.9rem', textAlign: 'center', maxWidth: '320px' }}>
                                            No legendary coins yet. Complete collections to unlock special coins!
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <p className={styles.coinExplainer}>
                                            These legendary coins were earned by completing entire collections.
                                        </p>
                                        <div className={styles.coinGrid}>
                                            {legendaryCoins.map((coin) => (
                                                <CoinBadge
                                                    key={coin.id}
                                                    badge={coin}
                                                    size="md"
                                                    showLabel={true}
                                                    onClick={handleCoinClick}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Collection Coins View */}
                        {coinFilter === 'collection' && (
                            <>
                                {collectionCoins.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--color-text-muted)' }}>
                                        <span style={{ fontSize: '2rem' }}>🎴</span>
                                        <p style={{ fontSize: '0.9rem', textAlign: 'center', maxWidth: '320px' }}>
                                            No collection coins yet. These are earned by viewing collections!
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <p className={styles.coinExplainer}>
                                            These coins were earned by viewing collections.
                                        </p>
                                        <div className={styles.coinGrid}>
                                            {collectionCoins.map((coin) => (
                                                <CoinBadge
                                                    key={coin.id}
                                                    badge={coin}
                                                    size="md"
                                                    showLabel={true}
                                                    onClick={handleCoinClick}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </section>
                )}

                {/* ── Watchlist Tab ── */}
                {activeTab === 'watchlist' && (
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <h2 className={styles.sectionTitle}>📺 Watchlist</h2>
                            {watchlistVisible && <span className={styles.sectionCount}>{watchlistItems.length} items</span>}
                        </div>

                        {!watchlistVisible ? (
                            <div className={styles.privateMessage}>
                                <span style={{ fontSize: '2rem' }}>🔒</span>
                                <p>This watchlist is private</p>
                            </div>
                        ) : watchlistItems.length === 0 ? (
                            <EmptyWatchlist />
                        ) : (
                            <>
                                {/* Filter Pills */}
                                <div className={styles.filterPills}>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'all' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('all')}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'movie' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('movie')}
                                    >
                                        🎬 Movies
                                    </button>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'tv' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('tv')}
                                    >
                                        📺 TV Shows
                                    </button>
                                </div>

                                {filteredWatchlist.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--color-text-muted)' }}>
                                        <span style={{ fontSize: '2rem' }}>🔍</span>
                                        <p style={{ fontSize: '0.9rem' }}>No {watchlistFilter === 'movie' ? 'movies' : 'TV shows'} found</p>
                                    </div>
                                ) : (
                                    <div className={styles.watchedGrid}>
                                        {filteredWatchlist.map((item) => (
                                            <Link
                                                key={item.id}
                                                to={`/movie/${item.media?.kind || 'movie'}/${item.media?.tmdb_id}`}
                                                className={styles.watchedCard}
                                            >
                                                <div className={styles.watchedPosterWrap}>
                                                    {item.media?.poster_url ? (
                                                        <img src={item.media.poster_url} alt={item.media.title} className={styles.watchedPoster} />
                                                    ) : (
                                                        <div className={styles.watchedPoster} style={{ background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            🎬
                                                        </div>
                                                    )}
                                                    {item.status && (
                                                        <div className={styles.statusBadge}>
                                                            {item.status === 'watched' ? '✓' : '👁'}
                                                        </div>
                                                    )}
                                                </div>
                                                <p className={styles.watchedTitle}>{item.media?.title || 'Untitled'}</p>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}

                {/* ── Join CTA (only shown for non-logged-in visitors) ── */}
                {!user && (
                    <section className={styles.joinCta} id="download">
                        <p className={styles.joinEyebrow}>🔖 Save · 🧠 Verify · 🪙 Earn</p>
                        <h2 className={styles.joinTitle}>Build your own collection.</h2>
                        <p className={styles.joinSub}>
                            Save what you want to watch. Pass a short challenge after you've seen it.
                            Earn a collectible coin that proves your taste.
                        </p>
                        <CTAButton variant="gold" size="lg" href="/register" icon="🪙">
                            Join Aluvo — Free
                        </CTAButton>
                    </section>
                )}
            </div>

            {isReportModalOpen && (
                <ReportModal
                    username={profileUser.username}
                    userId={profileUser.id}
                    onClose={() => setIsReportModalOpen(false)}
                />
            )}

            {/* Coin Detail Modal */}
            <CoinModal
                isOpen={isCoinModalOpen}
                onClose={handleCloseCoinModal}
                coin={selectedCoin}
            />

            {/* Avatar Modal */}
            <AvatarModal
                isOpen={isAvatarModalOpen}
                onClose={() => setIsAvatarModalOpen(false)}
                avatarUrl={profileUser?.avatar_url}
                displayName={profileUser?.display_name}
                username={profileUser?.username}
            />
            <AuthModal {...authModalProps} />
        </main>
    );
}

function EmptyCoins({ message = "No coins earned yet — take your first challenge to start earning coins." }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--color-text-muted)' }}>
            <span style={{ fontSize: '2rem' }}>🪙</span>
            <p style={{ fontSize: '0.9rem' }}>{message}</p>
        </div>
    );
}

function EmptyWatchlist() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--color-text-muted)' }}>
            <span style={{ fontSize: '2rem' }}>🎬</span>
            <p style={{ fontSize: '0.9rem' }}>Nothing in watchlist yet</p>
        </div>
    );
}

function ProfileSkeleton() {
    return (
        <div className={styles.page} style={{ paddingTop: 120 }}>
            <div className="container" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                    <LoadingSkeleton width="88px" height="88px" borderRadius="50%" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
                        <LoadingSkeleton height="28px" width="180px" />
                        <LoadingSkeleton height="16px" width="110px" />
                        <LoadingSkeleton height="14px" width="240px" />
                    </div>
                </div>
                <LoadingSkeleton height="88px" borderRadius="16px" />
                <LoadingSkeleton height="220px" borderRadius="16px" />
            </div>
        </div>
    );
}

const REASONS = [
    { key: 'spam',                  label: '🚫 Spam' },
    { key: 'harassment',            label: '😡 Harassment' },
    { key: 'inappropriate_content', label: '🔞 Inappropriate content' },
    { key: 'other',                 label: '❓ Other' },
];

function ReportModal({ username, userId, onClose }) {
    const [reason, setReason] = useState('');
    const [details, setDetails] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!reason) return;
        setSubmitting(true);
        try {
            await SafetyService.submitReport({
                reported_user_id: userId,
                reported_content_type: 'user',
                reason,
                details: details.trim() || undefined,
            });
            setDone(true);
        } catch {
            alert('Could not submit report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                {done ? (
                    <div className={styles.modalDone}>
                        <span style={{ fontSize: '2rem' }}>✅</span>
                        <h2>Report submitted</h2>
                        <p>Thanks — our team will review it shortly.</p>
                        <button className={styles.modalDoneBtn} onClick={onClose}>Close</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <h2 className={styles.modalTitle}>Report @{username}</h2>
                        <p className={styles.modalSub}>Why are you reporting this account?</p>

                        <div className={styles.reasonList}>
                            {REASONS.map(r => (
                                <label key={r.key} className={`${styles.reasonOption} ${reason === r.key ? styles.reasonSelected : ''}`}>
                                    <input
                                        type="radio"
                                        name="reason"
                                        value={r.key}
                                        checked={reason === r.key}
                                        onChange={() => setReason(r.key)}
                                        style={{ display: 'none' }}
                                    />
                                    {r.label}
                                </label>
                            ))}
                        </div>

                        <textarea
                            className={styles.detailsInput}
                            placeholder="Additional details (optional)"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            maxLength={500}
                            rows={3}
                        />
                        <p className={styles.charCount}>{details.length}/500</p>

                        <button
                            type="submit"
                            className={styles.submitReportBtn}
                            disabled={!reason || submitting}
                        >
                            {submitting ? 'Submitting…' : 'Submit report'}
                        </button>

                        <p className={styles.reportDisclaimer}>
                            We won't notify the account you're reporting.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}

export default Profile;
